import React, { useState, useEffect, useRef } from 'react';
import { pitchesApi, clientsApi, pitchingApi, googleApi } from '../utils/api';
import { useSendJobs } from '../context/SendJobsContext';
import { useToast } from '../hooks/useToast';

const STATUSES = [
  { key: 'all',         label: 'All' },
  { key: 'to_pitch',   label: 'To Pitch',    color: 'var(--text-muted)' },
  { key: 'pitched',    label: 'Pitched',      color: 'var(--blue)' },
  { key: 'followed_up',label: 'Followed Up',  color: 'var(--accent)' },
  { key: 'answered',   label: 'Answered',     color: 'var(--amber)' },
  { key: 'saved',      label: 'Said Yes',     color: 'var(--green)' },
  { key: 'offered',    label: 'Offered',      color: 'var(--green)' },
  { key: 'booked',     label: 'Booked',       color: 'var(--green)' },
  { key: 'form',        label: 'Form',         color: 'var(--amber)' },
  { key: 'form_filled', label: 'Form Filled',  color: 'var(--green)' },
  { key: 'declined',    label: 'Declined',     color: 'var(--red)' },
  { key: 'cancelled',   label: 'Cancelled',    color: 'var(--text-muted)' },
];

const STATUS_BADGES = {
  to_pitch:    { label: 'To Pitch',    cls: 'badge-muted' },
  pitched:     { label: 'Pitched',     cls: 'badge-blue' },
  followed_up: { label: 'Followed Up', cls: 'badge-purple' },
  answered:    { label: 'Answered',    cls: 'badge-amber' },
  saved:       { label: 'Said Yes ✓',  cls: 'badge-green' },
  offered:     { label: 'Offered',     cls: 'badge-green' },
  booked:      { label: 'Booked 🎙',   cls: 'badge-green' },
  form:        { label: 'Form',         cls: 'badge-amber' },
  form_filled: { label: 'Form Filled ✓', cls: 'badge-green' },
  declined:    { label: 'Declined',     cls: 'badge-red' },
  cancelled:   { label: 'Cancelled',   cls: 'badge-muted' },
};

// Same podcast = same client and same name OR same link — must match the server's isSamePodcast
// (sheetSync.js), which is what edits/status changes use to update every copy. Matching on the exact
// name here left copies with a different name (same link) showing stale values until a reload.
const normName = n => (n || '').toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');
const normUrl = u => {
  if (!u) return '';
  const appleId = (u.match(/[/?&]id(\d{6,})/i) || [])[1];
  if (appleId) return `apple:id:${appleId}`;
  return u.toLowerCase().trim().replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '').split('?')[0].split('#')[0];
};
const samePodcast = (a, b) => {
  if (a.clientId !== b.clientId) return false;
  if (normName(a.podcastName) && normName(a.podcastName) === normName(b.podcastName)) return true;
  const la = normUrl(a.podcastLink || a.podcastWebsite), lb = normUrl(b.podcastLink || b.podcastWebsite);
  return !!(la && la === lb);
};

// ─── Re-pitch cooldown label (under the status dropdown) ─────────────────────
// Cooldown is 12 months from last contact, across every tab; computed server-side.
function RepitchLabel({ pitch }) {
  const r = pitch.repitch;
  if (!r) return null;
  const style = { fontSize: 10, marginTop: 3, whiteSpace: 'nowrap' };
  if (r.state === 'repitch') {
    return <div style={{ ...style, color: 'var(--blue)' }} title="Last contact was over 12 months ago, so it can be pitched again">↻ Re-pitch</div>;
  }
  if (r.state === 'check_dates') {
    return <div style={{ ...style, color: 'var(--amber)' }} title="A pitch/follow-up date for this podcast (in some tab) can't be read, so it's blocked from pitching until the date is fixed in the sheet">Check dates</div>;
  }
  const finished = pitch.status === 'to_pitch' || (pitch.status === 'followed_up' && pitch.followUpCount >= 3);
  if (r.state === 'cooldown' && finished) {
    const until = new Date(r.cooldownUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return <div style={{ ...style, color: 'var(--text-muted)' }} title="Contacted within the last 12 months (in some tab), so it can't be pitched yet">Cooldown until {until}</div>;
  }
  return null;
}

// ─── Said Yes Modal ───────────────────────────────────────────────────────────
function SaidYesModal({ pitch, onClose, onSave }) {
  const toast = useToast();
  const [form, setForm] = useState({
    hostName: pitch.hostName || '',
    monthlyListeners: pitch.monthlyListeners || '',
    opportunityNotes: pitch.opportunityNotes || ''
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await pitchesApi.saidYes(pitch.id, form);
      onSave(res.data.pitch);
      if (res.data.sheetError) toast(`Saved in the app, but the sheet wasn't updated: ${res.data.sheetError}`, 'error');
      else toast('Saved! Added to your queue ✓', 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Error', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div>
            <h2>🎉 Said Yes!</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{pitch.podcastName}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div style={{ background: 'var(--green-soft)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 20, fontSize: 13 }}>
          Fill in the details below. This podcast will be saved to your queue — you can offer it to your client from the Send EOW tab.
        </div>
        <div className="form-group">
          <label>Host Name</label>
          <input value={form.hostName} onChange={e => set('hostName', e.target.value)} placeholder="Jonny Pardoe" autoFocus />
        </div>
        <div className="form-group">
          <label>Monthly Listeners</label>
          <input value={form.monthlyListeners} onChange={e => set('monthlyListeners', e.target.value)} placeholder="78.1K" />
        </div>
        <div className="form-group">
          <label>Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(shown in the opportunity email)</span></label>
          <textarea value={form.opportunityNotes} onChange={e => set('opportunityNotes', e.target.value)} rows={4} placeholder="e.g. Pay to play — $250 after interview. Great ratings — 320 on Apple (4.6★)" />
        </div>
        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Form Modal ──────────────────────────────────────────────────────────────
function FormModal({ pitch, googleAccounts, onClose, onSave }) {
  const toast = useToast();
  const [formUrl, setFormUrl] = useState(pitch.formUrl || '');
  const [formEmail, setFormEmail] = useState(pitch.formEmail || googleAccounts[0]?.email || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formUrl.trim()) { toast('Form URL is required', 'error'); return; }
    setSaving(true);
    try {
      const res = await pitchesApi.update(pitch.id, { status: 'form', formUrl: formUrl.trim(), formEmail });
      onSave(res.data);
      toast('Marked as Form ✓', 'success');
    } catch { toast('Error saving', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div>
            <h2>📋 Guest Intake Form</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{pitch.podcastName}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="form-group">
          <label>Form URL *</label>
          <input value={formUrl} onChange={e => setFormUrl(e.target.value)} placeholder="https://docs.google.com/forms/..." autoFocus />
        </div>
        {googleAccounts.length > 0 && (
          <div className="form-group">
            <label>Submit as</label>
            <select value={formEmail} onChange={e => setFormEmail(e.target.value)} style={{ width: 'auto' }}>
              {googleAccounts.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
            </select>
          </div>
        )}
        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Show-notes flags (read server-side from the sheet's Notes column, see noteFlags.js) ───
const PAY_TO_PLAY_BG = 'rgba(34,197,94,0.08)';
const untilText = until => until ? ` until ${new Date(until + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : '';

function NoteFlags({ noteInfo }) {
  if (!noteInfo || (!noteInfo.blockKey && !noteInfo.payToPlay)) return null;
  const tip = noteInfo.blockFromOtherClient ? `From another client's sheet: ${noteInfo.blockNote}` : (noteInfo.notes || noteInfo.blockNote || noteInfo.payNote);
  const tag = (text, color) => (
    <span title={tip} style={{ fontSize: 10, border: `1px solid ${color}`, color, borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{text}</span>
  );
  return (
    <>
      {noteInfo.blockKey && tag(`Not emailed${untilText(noteInfo.blockUntil)} · ${noteInfo.blockLabel}`, 'var(--red)')}
      {noteInfo.payToPlay && tag('Pay to play', 'var(--green)')}
    </>
  );
}

function NoteText({ notes }) {
  if (!notes) return null;
  return <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{notes}</div>;
}

// ─── Send Pitches Modal ───────────────────────────────────────────────────────
function SendPitchesModal({ client, tabs, onClose, onDelete, onPitchUpdated }) {
  const toast = useToast();
  const [sendType, setSendType] = useState('initialPitch');
  const [count, setCount] = useState(10);
  const [eligible, setEligible] = useState([]);
  const [noteBlocked, setNoteBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobId, setJobId] = useState(null);
  const { jobs, addJob, cancelJob: ctxCancelJob, dismissJob } = useSendJobs();
  const ctxJob = jobs.find(j => j.jobId === jobId);
  // cache last known job state so modal keeps showing "Done" even after context auto-dismisses
  const lastJobRef = useRef(null);
  if (ctxJob) lastJobRef.current = ctxJob;
  const job = ctxJob || (jobId ? lastJobRef.current : null);
  const sending = !!jobId && !job?.done;
  const [clientLinkInput, setClientLinkInput] = useState(client.clientLink || '');
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [rowFrom, setRowFrom] = useState('');
  const [rowTo, setRowTo] = useState('');
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [sendFromEmail, setSendFromEmail] = useState(client.googleAccountEmail || '');
  const [followUpFilterEmail, setFollowUpFilterEmail] = useState('');

  const [selectedTab, setSelectedTab] = useState(tabs && tabs.length > 0 ? tabs[0] : '');
  const defaultSubject = (() => {
    const email = (client.googleAccountEmail || '').toLowerCase();
    if (email.endsWith('@podcastcola.com') || email.endsWith('@thepodcastcola.com')) {
      return `Podcast Guest Opportunity – ${client.name}`;
    }
    return `Guest Feature Opportunity – ${client.name}`;
  })();
  const [subject, setSubject] = useState(defaultSubject);

  const TYPES = [
    { key: 'initialPitch', label: 'New Pitches',   desc: 'Not yet pitched' },
    { key: 'followUp1',    label: 'Follow-up 1',   desc: 'Pitched, no follow-up' },
    { key: 'followUp2',    label: 'Follow-up 2',   desc: 'Followed up once' },
    { key: 'followUp3',    label: 'Last Follow-up', desc: 'Followed up twice' },
  ];

  const isFollowUpType = sendType !== 'initialPitch';

  const rangeFrom = rowFrom ? parseInt(rowFrom) : null;
  const rangeTo = rowTo ? parseInt(rowTo) : null;
  const matchesFollowUpFilter = (p) => {
    if (!isFollowUpType || !followUpFilterEmail) return true;
    // Pitches with no recorded senderEmail (legacy, or account probed at send
    // time) must NOT be guessed into the client's default account — that guess
    // can be wrong and silently mixes another account's volume into this one.
    if (!p.senderEmail) return false;
    return p.senderEmail === followUpFilterEmail;
  };
  const rangeFilteredEligible = eligible.filter(p => {
    if (rangeFrom && p.sheetRowNum < rangeFrom) return false;
    if (rangeTo && p.sheetRowNum > rangeTo) return false;
    if (!matchesFollowUpFilter(p)) return false;
    return true;
  });

  const getFilteredIds = () => rangeFilteredEligible.slice(0, count).map(p => p.id);

  const handleSendClick = () => {
    if (isFollowUpType && !clientLinkInput) {
      setShowLinkPrompt(true);
      return;
    }
    handleSend();
  };

  const handleLinkSave = async () => {
    // Save clientLink to the client record
    try {
      await clientsApi.update(client.id, { ...client, clientLink: clientLinkInput });
      setShowLinkPrompt(false);
      handleSend();
    } catch (e) {
      toast('Could not save link, proceeding anyway', 'info');
      setShowLinkPrompt(false);
      handleSend();
    }
  };

  useEffect(() => {
    googleApi.getAccounts().then(r => {
      const accounts = r.data.accounts || [];
      setGoogleAccounts(accounts);
      if (!sendFromEmail && accounts.length > 0) setSendFromEmail(client.googleAccountEmail || accounts[0].email || '');
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      pitchingApi.eligible(client.id, sendType, selectedTab || null),
      pitchingApi.noteBlocked(client.id, sendType, selectedTab || null).catch(() => ({ data: [] }))
    ])
      .then(([res, blockedRes]) => {
        if (cancelled) return;
        setEligible(res.data);
        setNoteBlocked(blockedRes.data || []);
        setCount(Math.min(10, res.data.length));
      })
      .catch(() => { if (!cancelled) toast('Could not load eligible podcasts', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sendType, selectedTab]);

  useEffect(() => {
    const from = rowFrom ? parseInt(rowFrom) : null;
    const to = rowTo ? parseInt(rowTo) : null;
    const filtered = eligible.filter(p => {
      if (from && p.sheetRowNum < from) return false;
      if (to && p.sheetRowNum > to) return false;
      if (!matchesFollowUpFilter(p)) return false;
      return true;
    });
    if (!from && !to && !(isFollowUpType && followUpFilterEmail)) {
      setCount(Math.min(10, filtered.length));
      return;
    }
    setCount(filtered.length);
  }, [rowFrom, rowTo, eligible, followUpFilterEmail, isFollowUpType]);

  // Reset the account filter when switching send type or tab so stale filters don't hide results
  useEffect(() => {
    setFollowUpFilterEmail('');
  }, [sendType, selectedTab]);

  const handleSend = async () => {
    const pitchIds = getFilteredIds();
    if (!pitchIds.length) return;
    const response = await fetch(`/api/pitching/send/${client.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sendType, pitchIds, tabName: selectedTab || null, subject, accountEmail: sendFromEmail || null })
    });
    const data = await response.json();
    if (!response.ok) {
      toast(data.error || 'Could not start send', 'error');
      return;
    }
    lastJobRef.current = null;
    addJob(data.jobId, client.name, pitchIds.length);
    setJobId(data.jobId);
  };

  const handleCancel = () => {
    if (jobId) ctxCancelJob(jobId);
  };

  const filteredToSend = rangeFilteredEligible.slice(0, count);
  const rangeFilteredBlocked = noteBlocked.filter(p => {
    if (rangeFrom && p.sheetRowNum < rangeFrom) return false;
    if (rangeTo && p.sheetRowNum > rangeTo) return false;
    return true;
  });

  const handleDeleteHere = async (id) => {
    const deletedIds = await onDelete(id);
    if (!deletedIds) return;
    setEligible(prev => prev.filter(p => !deletedIds.includes(p.id)));
    setNoteBlocked(prev => prev.filter(p => !deletedIds.includes(p.id)));
  };

  const handleMoveToForm = async (p) => {
    try {
      const res = await pitchesApi.update(p.id, { status: 'form', formUrl: p.formUrl, formEmail: sendFromEmail || client.googleAccountEmail || '' });
      onPitchUpdated(res.data);
      setNoteBlocked(prev => prev.filter(x => x.id !== p.id));
      toast('Moved to Form ✓', 'success');
    } catch { toast('Could not move to Form', 'error'); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>📤 Send Pitches — {client.name}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        {showLinkPrompt ? (
          <div>
            <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
              Follow-up emails include a link to <strong style={{ color: 'var(--text-primary)' }}>{client.name}</strong>'s profile. Enter it below — saved for all future follow-ups.
            </div>
            <div className="form-group">
              <label>Client Link</label>
              <input value={clientLinkInput} onChange={e => setClientLinkInput(e.target.value)} placeholder="https://..." autoFocus />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Leave empty to send without a hyperlink</div>
            </div>
            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowLinkPrompt(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleLinkSave}>{clientLinkInput ? 'Save & Send' : 'Send Without Link'}</button>
            </div>
          </div>
        ) : jobId ? (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, background: 'var(--green-soft)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm)', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--green)' }}>{job?.sent ?? 0}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sent</div>
              </div>
              <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{(job?.total ?? 0) - (job?.sent ?? 0) - (job?.failed ?? 0)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Remaining</div>
              </div>
              {(job?.failed ?? 0) > 0 && (
                <div style={{ flex: 1, background: 'var(--red-soft)', borderRadius: 'var(--radius-sm)', padding: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--red)' }}>{job?.failed ?? 0}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Failed</div>
                </div>
              )}
            </div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px', height: 180, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12, marginBottom: 16 }}>
              {(job?.log ?? []).map((l, i) => (
                <div key={i} style={{ color: l.ok === true ? 'var(--green)' : l.ok === false ? 'var(--red)' : 'var(--text-muted)', marginBottom: 3 }}>{l.msg}</div>
              ))}
            </div>
            <div className="flex-center gap-2">
              {!job?.done ? (
                <button className="btn btn-danger" onClick={handleCancel}>Stop</button>
              ) : (
                <button className="btn btn-primary" onClick={() => { dismissJob(jobId); onClose(); }}>Done</button>
              )}
            </div>
          </>
        ) : (
          <>
            {!isFollowUpType && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Email Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} placeholder={defaultSubject} />
              </div>
            )}
            {googleAccounts.length > 1 && !isFollowUpType && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Send from</label>
                <select value={sendFromEmail} onChange={e => setSendFromEmail(e.target.value)} style={{ width: 'auto' }}>
                  {googleAccounts.map(a => (
                    <option key={a.email} value={a.email}>{a.email}</option>
                  ))}
                </select>
              </div>
            )}
            {googleAccounts.length > 1 && isFollowUpType && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Filter by sending account</label>
                <select value={followUpFilterEmail} onChange={e => setFollowUpFilterEmail(e.target.value)} style={{ width: 'auto' }}>
                  <option value="">All accounts</option>
                  {googleAccounts.map(a => (
                    <option key={a.email} value={a.email}>{a.email}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Follow-ups always send from whichever account sent the original pitch — pick one here to only show (and send) the follow-ups tied to it, so you can spread sends across accounts.
                </div>
              </div>
            )}
            {tabs && tabs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ marginBottom: 8, display: 'block' }}>Sheet Tab</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {tabs.map(t => (
                    <button key={t} className={`btn btn-sm ${selectedTab === t ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSelectedTab(t)}>{t}</button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {TYPES.map(t => (
                <button key={t.key} onClick={() => setSendType(t.key)} style={{
                  padding: '10px 16px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: `1px solid ${sendType === t.key ? 'var(--accent)' : 'var(--border)'}`,
                  background: sendType === t.key ? 'var(--accent-soft)' : 'var(--bg-elevated)', textAlign: 'left'
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: sendType === t.key ? 'var(--accent)' : 'var(--text-primary)' }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.desc}</div>
                </button>
              ))}
            </div>
            {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div> : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
                    <div>
                      <label>How many to send? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({rangeFilteredEligible.length} available)</span></label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                        <input type="number" value={count} min={1} max={rangeFilteredEligible.length}
                          onChange={e => setCount(Math.max(1, Math.min(rangeFilteredEligible.length, parseInt(e.target.value) || 1)))}
                          style={{ width: 80 }} />
                        {count > 1 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>~{Math.round((count-1) * 52.5 / 60)} min with delays</span>}
                      </div>
                    </div>
                    <div>
                      <label>Row Range <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                        <input type="number" value={rowFrom} onChange={e => setRowFrom(e.target.value)} placeholder="From row" style={{ width: 90 }} min={1} />
                        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>to</span>
                        <input type="number" value={rowTo} onChange={e => setRowTo(e.target.value)} placeholder="To row" style={{ width: 90 }} min={1} />
                      </div>
                    </div>
                  </div>
                </div>
                {filteredToSend.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>No podcasts ready for this type/range.</div>
                ) : (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ marginBottom: 8, display: 'block' }}>Will send to:</label>
                    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {filteredToSend.map((p, i) => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: p.payToPlay ? PAY_TO_PLAY_BG : 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                          <span style={{ color: 'var(--text-muted)', width: 20, fontSize: 11 }}>{i+1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>{p.podcastName}{p.payToPlay && <span style={{ fontSize: 10, color: 'var(--green)', marginLeft: 8 }}>Pay to play</span>}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.hostEmail}</div>
                            <NoteText notes={p.notes} />
                            {p.payNoteOther && <NoteText notes={`From another client's sheet: ${p.payNoteOther}`} />}
                          </div>
                          {isFollowUpType && googleAccounts.length > 1 && (
                            <span style={{ fontSize: 11, color: p.senderEmail ? 'var(--text-muted)' : 'var(--amber)' }}>{p.senderEmail || 'Unknown — resolved at send'}</span>
                          )}
                          {!p.hasPitchText && sendType === 'initialPitch' && <span style={{ fontSize: 11, color: 'var(--amber)' }}>⚠ No pitch text</span>}
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteHere(p.id)}>Delete</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {rangeFilteredBlocked.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ marginBottom: 4, display: 'block' }}>Not emailed because of show notes ({rangeFilteredBlocked.length})</label>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                      Their notes say to pitch through a form, that they aren't taking guests, that the email is bad, or that the show isn't for this client. Ones with a date come back on their own on that day.
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {rangeFilteredBlocked.map(p => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13, opacity: 0.85 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>{p.podcastName} <span style={{ fontSize: 10, color: 'var(--red)', marginLeft: 6 }}>{p.noteBlock}{untilText(p.noteBlockUntil)}</span>{p.payToPlay && <span style={{ fontSize: 10, color: 'var(--green)', marginLeft: 6 }}>Pay to play</span>}</div>
                            <NoteText notes={p.notes} />
                            {p.noteBlockOther && <NoteText notes={`From another client's sheet: ${p.noteBlockOther}`} />}
                          </div>
                          {p.noteBlockKey === 'form' && p.formUrl && sendType === 'initialPitch' && (
                            <button className="btn btn-sm btn-secondary" onClick={() => handleMoveToForm(p)}>Move to Form</button>
                          )}
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteHere(p.id)}>Delete</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSendClick} disabled={filteredToSend.length === 0}>
                    🚀 Send {filteredToSend.length} {TYPES.find(t => t.key === sendType)?.label}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Edit Podcast Modal ─────────────────────────────────────────────────────
function EditPitchModal({ pitch, onClose, onSave }) {
  const toast = useToast();
  const [hostEmail, setHostEmail] = useState(pitch.hostEmail || '');
  const [hostName, setHostName] = useState(pitch.hostName || '');
  const [category, setCategory] = useState(pitch.category || '');
  const [notes, setNotes] = useState(pitch.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const fields = {};
    if (hostEmail !== (pitch.hostEmail || '')) fields.hostEmail = hostEmail;
    if (hostName !== (pitch.hostName || '')) fields.hostName = hostName;
    if (category !== (pitch.category || '')) fields.category = category;
    if (notes !== (pitch.notes || '')) fields.notes = notes;

    if (!Object.keys(fields).length) { onClose(); return; }

    setSaving(true);
    try {
      const res = await pitchesApi.update(pitch.id, fields);
      onSave(res.data, fields);
      toast('Saved ✓ — syncing to the master sheet and any other client list with this podcast', 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Error saving', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <h2>{pitch.podcastName}</h2>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>
          Changes here also propagate to the master sheet and any other client's list with this same podcast.
        </p>
        <div className="form-group">
          <label>Host Email</label>
          <input value={hostEmail} onChange={e => setHostEmail(e.target.value)} placeholder="host@example.com" autoFocus />
        </div>
        <div className="form-group">
          <label>Host Name</label>
          <input value={hostName} onChange={e => setHostName(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Category</label>
          <input value={category} onChange={e => setCategory(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        </div>
        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// Session cache — persists across navigation, cleared on page refresh
const sessionCache = {};

// ─── Main Podcasts Page ───────────────────────────────────────────────────────
export default function Podcasts() {
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [activeClient, setActiveClient] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saidYesModal, setSaidYesModal] = useState(null);
  const [formModal, setFormModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [sendModal, setSendModal] = useState(false);
  const [unmatched, setUnmatched] = useState([]);
  const [syncError, setSyncError] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [tabFilter, setTabFilter] = useState('all');

  // Store tabs per client so switching clients updates tabs from cache

  useEffect(() => {
    googleApi.getStatus().then(r => setGoogleAccounts(r.data.accounts || [])).catch(() => {});
  }, []);

  // On mount: load clients, load pitches from DB, then sync in background
  useEffect(() => {
    clientsApi.getAll().then(r => {
      const active = r.data.filter(c => !c.archived);
      setClients(active);
      if (active.length > 0) setActiveClient(active[0].id);
    });
  }, []);

  useEffect(() => {
    if (!activeClient) return;
    let cancelled = false;

    // Restore from cache instantly — no sync needed
    if (sessionCache[activeClient]) {
      const c = sessionCache[activeClient];
      setTabFilter('all');
      setPitches(c.pitches || []);
      setTabs(c.tabs || []);
      setUnmatched(c.unmatched || []);
      setSyncError(c.syncError || null);
      setLoading(false);
      return;
    }

    // First visit this session — clear stale data immediately, then sync
    setPitches([]);
    setTabs([]);
    setTabFilter('all');
    setLoading(true);
    setSyncing(true);
    const clientAtStart = activeClient;
    pitchingApi.sync(clientAtStart)
      .then(r => {
        if (cancelled) return;
        const unmatched = r.data.unmatched?.length ? r.data.unmatched : [];
        setUnmatched(unmatched);
        sessionCache[clientAtStart] = { ...(sessionCache[clientAtStart] || {}), unmatched, syncError: null };
        setSyncError(null);
      })
      .catch(e => {
        if (cancelled) return;
        const err = e.response?.data?.error || e.message || 'Sync failed';
        setSyncError(err);
        sessionCache[clientAtStart] = { ...(sessionCache[clientAtStart] || {}), syncError: err };
      })
      .finally(() => {
        if (cancelled) return;
        setSyncing(false);
        Promise.all([
          pitchingApi.getTabs(clientAtStart).catch(() => ({ data: { tabs: [] } })),
          pitchesApi.getByClient(clientAtStart)
        ]).then(([tabsRes, pitchesRes]) => {
          if (cancelled) return;
          const tabs = tabsRes.data.tabs || [];
          const pitches = pitchesRes.data || [];
          setTabs(tabs);
          setPitches(pitches);
          sessionCache[clientAtStart] = { ...sessionCache[clientAtStart], tabs, pitches };
          setLoading(false);
        });
      });
    return () => { cancelled = true; };
  }, [activeClient]);

  const cachePitches = (updater) => {
    setPitches(prev => {
      const next = updater(prev);
      if (sessionCache[activeClient]) sessionCache[activeClient].pitches = next;
      return next;
    });
  };

  const handleManualSync = async () => {
    if (!activeClient) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const syncRes = await pitchingApi.sync(activeClient);
      const newUnmatched = syncRes.data.unmatched?.length ? syncRes.data.unmatched : [];
      const [tabsRes, pitchesRes] = await Promise.all([
        pitchingApi.getTabs(activeClient).catch(() => ({ data: { tabs: [] } })),
        pitchesApi.getByClient(activeClient)
      ]);
      const tabs = tabsRes.data.tabs || [];
      const pitches = pitchesRes.data || [];
      setTabs(tabs);
      setPitches(pitches);
      setUnmatched(newUnmatched);
      sessionCache[activeClient] = { ...sessionCache[activeClient], tabs, pitches, unmatched: newUnmatched, syncError: null };
      toast('Synced ✓', 'success');
    } catch (e) {
      const err = e.response?.data?.error || e.message || 'Sync failed';
      setSyncError(err);
      sessionCache[activeClient] = { ...(sessionCache[activeClient] || {}), syncError: err };
      toast('Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    if (status === 'saved') { setSaidYesModal(pitches.find(p => p.id === id)); return; }
    if (status === 'form') { setFormModal(pitches.find(p => p.id === id)); return; }
    try {
      const res = await pitchesApi.update(id, { status });
      const updated = res.data;
      const syncFields = { status: updated.status, offeredToClient: updated.offeredToClient, updatedAt: updated.updatedAt };
      cachePitches(prev => prev.map(p => {
        if (p.id === id) return { ...p, ...updated };
        if (samePodcast(p, updated)) return { ...p, ...syncFields };
        return p;
      }));
    } catch (e) { toast('Error updating', 'error'); }
  };

  const handleFormSave = (updated) => {
    cachePitches(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    setFormModal(null);
  };

  const handleSaidYesSave = (updated) => {
    // host/listeners typed in Said Yes are applied to every copy server-side too
    const syncFields = { status: updated.status, offeredToClient: updated.offeredToClient, updatedAt: updated.updatedAt, hostName: updated.hostName, monthlyListeners: updated.monthlyListeners };
    cachePitches(prev => prev.map(p => {
      if (p.id === updated.id) return { ...p, ...updated };
      if (samePodcast(p, updated)) return { ...p, ...syncFields };
      return p;
    }));
    setSaidYesModal(null);
  };

  const handleEditSave = (updated, changedFields) => {
    cachePitches(prev => prev.map(p => {
      if (p.id === updated.id) return { ...p, ...updated };
      if (samePodcast(p, updated)) return { ...p, ...changedFields };
      return p;
    }));
    setEditModal(null);
  };

  // Returns the deleted ids (every tab copy), or null if cancelled/failed — the send popup uses them too
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this podcast from the list? This removes it from every tab it appears in and from the sheet.')) return null;
    try {
      const res = await pitchesApi.delete(id);
      const deletedIds = res.data.deletedIds || [id];
      cachePitches(prev => prev.filter(p => !deletedIds.includes(p.id)));
      toast('Deleted', 'success');
      return deletedIds;
    } catch (e) { toast(e.response?.data?.error || 'Could not delete', 'error'); return null; }
  };

  const currentClient = clients.find(c => c.id === activeClient);

  const filtered = pitches
    .filter(p => tabFilter === 'all' || p.sheetTab === tabFilter)
    .filter(p => filter === 'all' || p.status === filter)
    .filter(p => !search ||
      p.podcastName.toLowerCase().includes(search.toLowerCase()) ||
      (p.hostName || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (filter === 'offered') {
        const da = new Date(a.opportunityDraftedAt || a.updatedAt || 0);
        const db2 = new Date(b.opportunityDraftedAt || b.updatedAt || 0);
        return db2 - da;
      }
      const tabDiff = tabs.indexOf(a.sheetTab) - tabs.indexOf(b.sheetTab);
      if (tabDiff !== 0) return tabDiff;
      const rowA = a.sheetRowNum ?? Infinity;
      const rowB = b.sheetRowNum ?? Infinity;
      return rowA - rowB;
    });

  const tabFilteredPitches = tabFilter === 'all' ? pitches : pitches.filter(p => p.sheetTab === tabFilter);
  const counts = {};
  STATUSES.forEach(s => {
    counts[s.key] = s.key === 'all' ? tabFilteredPitches.length : tabFilteredPitches.filter(p => p.status === s.key).length;
  });

  return (
    <div className="page-content">
      <div className="page-header flex-between">
        <div>
          <h1>Podcasts</h1>
          <p>Full pitch list for each client — track, search, and act</p>
        </div>
        <div className="flex-center gap-2">
          <button className="btn btn-secondary" onClick={handleManualSync} disabled={syncing}>
            {syncing ? '↻ Syncing...' : '↻ Sync Data'}
          </button>
          <button className="btn btn-primary" onClick={() => setSendModal(true)} disabled={!currentClient}>
            📤 Pitch Podcasts
          </button>
        </div>
      </div>

      {/* Client tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
        {clients.map(c => (
          <button key={c.id}
            className={`btn btn-sm ${activeClient === c.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveClient(c.id)}>
            {c.name}
          </button>
        ))}
      </div>

      {/* Sync error */}
      {syncError && (
        <div style={{ background: 'var(--red-soft)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          <strong style={{ color: 'var(--red)' }}>⚠ Could not sync with Google Sheet:</strong>
          <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>{syncError}</span>
          <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>— Showing last known data. Check your Google connection in Settings.</span>
        </div>
      )}

      {/* Tab filter */}
      {tabs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${tabFilter === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTabFilter('all')}>All Tabs</button>
          {tabs.map(t => (
            <button key={t} className={`btn btn-sm ${tabFilter === t ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTabFilter(t)}>{t}</button>
          ))}
        </div>
      )}

      {/* Unmatched warning */}
      {unmatched.length > 0 && (
        <div style={{ position: 'relative', background: 'var(--amber-soft)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', paddingRight: 32, marginBottom: 16, fontSize: 13 }}>
          <button onClick={() => { setUnmatched([]); if (sessionCache[activeClient]) sessionCache[activeClient].unmatched = []; }}
            style={{ position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, color: 'var(--text-muted)', padding: 2 }}
            title="Dismiss">×</button>
          <strong style={{ color: 'var(--amber)' }}>⚠ {unmatched.length} podcasts from the booking report couldn't be matched:</strong>
          <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>{unmatched.map((u, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              · <strong>{u.name || '(no name)'}</strong>
              {u.link && <div style={{ marginLeft: 12, fontSize: 11, opacity: 0.6, wordBreak: 'break-all' }}>{u.link}</div>}
            </div>
          ))}</div>
        </div>
      )}

      {/* Status filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUSES.filter(s => s.key === 'all' || counts[s.key] > 0).map(s => (
          <button key={s.key}
            className={`btn btn-sm ${filter === s.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setFilter(s.key)}>
            {s.label} ({counts[s.key]})
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by podcast name or host..." style={{ maxWidth: 360 }} />
      </div>

      {/* Syncing indicator */}
      {syncing && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>↻ Syncing with sheet...</div>}

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📻</div>
          <h3>{search ? 'No results' : 'No podcasts'}</h3>
          <p>{search ? 'Try a different search' : 'Import from the Google Sheet to get started'}</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Podcast</th>
                <th>Host</th>
                <th>Listeners</th>
                <th>Status</th>
                <th>Pitched</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(pitch => {
                const badge = STATUS_BADGES[pitch.status] || STATUS_BADGES.to_pitch;
                const canSayYes = ['pitched', 'followed_up', 'answered'].includes(pitch.status);
                const canCancel = pitch.status === 'offered';
                return (
                  <tr key={pitch.id} style={pitch.noteInfo?.payToPlay ? { background: PAY_TO_PLAY_BG } : undefined}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{pitch.podcastName}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center' }}>
                        {(pitch.podcastLink || pitch.podcastWebsite) && (
                          <a href={pitch.podcastLink || pitch.podcastWebsite} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--accent)' }}>↗ Link</a>
                        )}
                        {pitch.sheetTab && tabs.length > 0 && (
                          <span style={{ fontSize: 10, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', color: 'var(--text-muted)' }}>{pitch.sheetTab}</span>
                        )}
                        <NoteFlags noteInfo={pitch.noteInfo} />
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{pitch.hostName || '—'}</div>
                      {pitch.hostEmail && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{pitch.hostEmail}</div>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{pitch.monthlyListeners || '—'}</td>
                    <td>
                      <select value={pitch.status} onChange={e => handleStatusChange(pitch.id, e.target.value)}
                        style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                        {STATUSES.filter(s => s.key !== 'all').map(s => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                      <RepitchLabel pitch={pitch} />
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {pitch.initialPitchDate || '—'}
                    </td>
                    <td>
                      <div className="flex-center gap-1">
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditModal(pitch)}>Edit</button>
                        {canSayYes && (
                          <button className="btn btn-sm btn-secondary"
                            style={{ color: 'var(--green)', borderColor: 'rgba(34,197,94,0.3)' }}
                            onClick={() => setSaidYesModal(pitch)}>
                            🎉 Said Yes
                          </button>
                        )}
                        {canCancel && (
                          <button className="btn btn-sm btn-secondary"
                            style={{ color: 'var(--text-muted)', fontSize: 11 }}
                            onClick={() => handleStatusChange(pitch.id, 'cancelled')}>
                            ✕ Cancel
                          </button>
                        )}
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(pitch.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {saidYesModal && (
        <SaidYesModal pitch={saidYesModal} onClose={() => setSaidYesModal(null)} onSave={handleSaidYesSave} />
      )}
      {editModal && (
        <EditPitchModal pitch={editModal} onClose={() => setEditModal(null)} onSave={handleEditSave} />
      )}
      {formModal && (
        <FormModal pitch={formModal} googleAccounts={googleAccounts} onClose={() => setFormModal(null)} onSave={handleFormSave} />
      )}
      {sendModal && currentClient && (
        <SendPitchesModal client={currentClient} tabs={tabs} onClose={() => setSendModal(false)} onDelete={handleDelete} onPitchUpdated={handleFormSave} />
      )}
    </div>
  );
}
